/**
 * Card reader pages. Edit photos in each item's `images` map:
 *   hero      — top of the page
 *   card      — thumbnail on /card-readers
 *   section1  — first section
 *   section2  — second section  (example: stripe-m2 section2)
 */
export const cardReadersHub = {
  path: '/card-readers',
  images: {
    hero: '/hardware-terminal.webp',
  },
  title: 'Stripe Terminal Card Readers',
  seoTitle: 'Stripe Terminal Card Readers for Restaurant POS | Tillio',
  seoDescription:
    'Pair Stripe Terminal readers with Tillio POS—S700, M2, WisePOS E, WisePad 3, and more. Chip, swipe, and tap on the Stripe account you already use.',
  intro:
    'Tillio works with Stripe Terminal hardware so your restaurant can take chip, swipe, and contactless payments at the counter or tableside. Choose a reader that fits your floor, then connect your existing Stripe account.',
}

export const cardReaders = [
  {
    slug: 'stripe-m2',
    path: '/card-readers/stripe-m2',
    images: {
      hero: '/hardware-terminal.webp',
      card: '/hardware-terminal.webp',
      section1: '/hardware-terminal.webp', // Built for roaming service
      section2: '/showcase-payments.webp', // Works with Tillio checkout
    },
    name: 'Stripe Reader M2',
    seoTitle: 'Stripe Reader M2 for Restaurant POS | Tillio',
    seoDescription:
      'Use the Stripe Reader M2 with Tillio POS for portable Bluetooth chip, swipe, and contactless payments—ideal for tableside and food trucks in the US.',
    connectivity: ['Bluetooth'],
    platforms: ['iOS', 'Android'],
    recommended: true,
    intro:
      'A compact Bluetooth mobile reader for tableside payments, patios, and flexible floor service. Pair the M2 with Tillio and your connected Stripe account for chip, swipe, and tap.',
    sections: [
      {
        title: 'Built for roaming service',
        description:
          'Carry the M2 to the table, close the check in Tillio, and present the reader for payment—without walking guests back to a fixed terminal.',
        bullets: [
          'Bluetooth connection to your POS tablet',
          'Chip, swipe, and contactless support',
          'Light enough for servers to carry all shift',
          'Strong fit for US restaurants and mobile setups',
        ],
      },
      {
        title: 'Works with Tillio checkout',
        description:
          'Payment amounts come from the open ticket. Staff stay in the same POS they use for orders and tables—Stripe handles the card rail.',
        bullets: [
          'Totals from the Tillio check',
          'Tips and split checks in the POS flow',
          '1.1% Tillio flat rate + Stripe fees',
          'Connect your existing Stripe account once',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'point-of-sale', 'table-management'],
  },
  {
    slug: 'stripe-s700',
    path: '/card-readers/stripe-s700',
    images: {
      hero: '/hardware-terminal.webp',
      card: '/hardware-terminal.webp',
      section1: '/hardware-terminal.webp', // Front-counter presence
      section2: '/feature-payments.webp', // Restaurant-ready with Stripe
    },
    name: 'Stripe Reader S700',
    seoTitle: 'Stripe Reader S700 & S710 for Restaurant POS | Tillio',
    seoDescription:
      'Countertop Stripe Reader S700 (and S710 with cellular backup) for Tillio POS—customer display, tipping, chip, swipe, and tap on your Stripe account.',
    connectivity: ['Wi‑Fi', 'Ethernet', 'Cellular'],
    platforms: ['iOS', 'Android'],
    recommended: true,
    intro:
      'A countertop smart reader with a customer-facing display for tipping, chip, swipe, and tap. The S710 adds cellular backup when Wi‑Fi is unreliable—same Tillio checkout experience either way.',
    sections: [
      {
        title: 'Front-counter presence',
        description:
          'Guests see the amount and tip prompts on the reader while your team rings the order on Tillio. Ideal for busy café counters and host-stand checkouts.',
        bullets: [
          'Customer-facing display for tips and confirmation',
          'Wi‑Fi or Ethernet for stable countertop use',
          'S710 option with cellular failover',
          'Chip, swipe, and contactless in one device',
        ],
      },
      {
        title: 'Restaurant-ready with Stripe',
        description:
          'Connect your Stripe account to Tillio, register the reader through Stripe Terminal, and keep payouts and disputes in the Dashboard you already use.',
        bullets: [
          'Stripe Terminal integration in Tillio',
          'PCI-ready card handling via Stripe',
          '1.1% Tillio flat rate + Stripe fees',
          'Pairs with printers, stands, and kitchen displays',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'point-of-sale', 'analytics'],
  },
  {
    slug: 'wisepos',
    path: '/card-readers/wisepos',
    images: {
      hero: '/hardware-terminal.webp',
      card: '/hardware-terminal.webp',
      section1: '/hardware-terminal.webp', // Smart reader for high volume
      section2: '/showcase-payments.webp', // Same Stripe account, restaurant POS
    },
    name: 'BBPOS WisePOS E',
    seoTitle: 'BBPOS WisePOS E Card Reader for Tillio POS',
    seoDescription:
      'Pair the BBPOS WisePOS E smart reader with Tillio POS for countertop tipping, chip, swipe, and tap—powered by Stripe Terminal on your Stripe account.',
    connectivity: ['Wi‑Fi', 'Ethernet'],
    platforms: ['iOS', 'Android'],
    recommended: true,
    intro:
      'A reliable countertop smart reader with on-screen tipping. Connect over Wi‑Fi or an ethernet dock for high-volume service while Tillio runs the restaurant workflow.',
    sections: [
      {
        title: 'Smart reader for high volume',
        description:
          'On-screen tipping and a stable countertop footprint keep lines moving. Guests tap or insert while your POS ticket stays the source of truth.',
        bullets: [
          'Customer display with tip prompts',
          'Wi‑Fi or ethernet dock connectivity',
          'Chip, swipe, and contactless',
          'Designed for counter and pass-through setups',
        ],
      },
      {
        title: 'Same Stripe account, restaurant POS',
        description:
          'Authorize Tillio with Stripe Connect, then use WisePOS E like any other supported Terminal reader—funds and reporting stay with Stripe.',
        bullets: [
          'Stripe Terminal compatible',
          'Works with Tillio order and table flows',
          '1.1% Tillio flat rate + Stripe fees',
          'See the full hardware catalog for printers and stands',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'point-of-sale', 'kitchen-display'],
  },
  {
    slug: 'wisepad',
    path: '/card-readers/wisepad',
    images: {
      hero: '/hardware-terminal.webp',
      card: '/hardware-terminal.webp',
      section1: '/hardware-terminal.webp', // Tableside and outdoor ready
      section2: '/feature-payments.webp', // Integrated with Tillio + Stripe
    },
    name: 'BBPOS WisePad 3',
    seoTitle: 'BBPOS WisePad 3 Card Reader for Tillio POS',
    seoDescription:
      'Portable BBPOS WisePad 3 Bluetooth reader for Tillio POS—chip, swipe, and contactless tableside payments on your connected Stripe account.',
    connectivity: ['Bluetooth'],
    platforms: ['iOS', 'Android'],
    recommended: false,
    intro:
      'A portable Bluetooth reader for chip, swipe, and contactless—built for roaming service, outdoor venues, and anywhere a fixed terminal doesn’t fit.',
    sections: [
      {
        title: 'Tableside and outdoor ready',
        description:
          'Take the check to the guest. Servers keep the tablet in hand, present the WisePad 3, and finish payment without a trip to the bar or host stand.',
        bullets: [
          'Bluetooth pairing with your POS device',
          'Chip, swipe, and contactless',
          'Portable for patios, events, and roaming FOH',
          'Complements countertop smart readers elsewhere in the room',
        ],
      },
      {
        title: 'Integrated with Tillio + Stripe',
        description:
          'Ring the order in Tillio, send the amount to the reader, and settle on the Stripe account you connected—no duplicate entry.',
        bullets: [
          'Payment amounts from the open check',
          'Tips and receipts in the POS workflow',
          'Stripe Terminal security and payouts',
          '1.1% Tillio flat rate + Stripe fees',
        ],
      },
    ],
    relatedFeatureSlugs: ['payments', 'table-management', 'point-of-sale'],
  },
]

export function getCardReader(slug) {
  return cardReaders.find((r) => r.slug === slug)
}
